import instance from "./instance";
import { ENV } from "@/constants/env";
import {
  ChatContentDto,
  CreateChatContentDto,
  UpdateChatContentDto,
  ChatContentListRequestDto,
} from "@/dto/ChatContentDto";
import { ResultListDto } from "@/dto/ResponseDto";

const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}`;

export const superChatContentApi = {
  /**
   * 컨텐츠 목록 조회
   */
  async getList(
    params: ChatContentListRequestDto = {}
  ): Promise<ResultListDto<ChatContentDto>> {
    const apiUrl = `${PREFIX_PATH}/qna-content`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "GET",
      params,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const response = await instance.get(apiUrl, { params });

    console.log("[API_RESPONSE]", {
      url: apiUrl,
      dataStructure: response ? Object.keys(response) : "null/undefined",
      dataCount: response?.list?.length || 0,
      fullResponse: response,
      hasData: !!response,
    });

    // 응답 데이터 검증 (인터셉터에서 이미 response.data를 반환함)
    if (!response) {
      console.error("[API_ERROR]", "Response is null or undefined");
      throw new Error("No data received from server");
    }

    // 인터셉터에서 이미 response.data를 반환하므로 직접 사용
    return response;
  },

  /**
   * 컨텐츠 상세 조회
   */
  async getDetail(id: number): Promise<ChatContentDto> {
    const apiUrl = `${PREFIX_PATH}/qna-content/${id}`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "GET",
      id,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const response = await instance.get(apiUrl);

    console.log("[API_RESPONSE]", {
      url: apiUrl,
      dataStructure: response ? Object.keys(response) : "null/undefined",
      fullResponse: response,
      hasData: !!response,
      success: response?.success,
      message: response?.message,
      data: response?.data,
    });

    // 응답 데이터 검증
    if (!response) {
      console.error("[API_ERROR]", "Response is null or undefined");
      throw new Error("No data received from server");
    }

    // 백엔드 응답 구조: {success: true, message: "...", data: {...}}
    // success 필드 체크
    if (response.success === false) {
      throw new Error(response.message || "API request failed");
    }

    // data 필드 반환
    return response.data;
  },

  /**
   * 컨텐츠 생성
   */
  async create(data: CreateChatContentDto): Promise<ChatContentDto> {
    const apiUrl = `${PREFIX_PATH}/qna-content`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "POST",
      data,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const response = await instance.post(apiUrl, data);

    console.log("[API_RESPONSE]", {
      url: apiUrl,
      dataStructure: response ? Object.keys(response) : "null/undefined",
      fullResponse: response,
      hasData: !!response,
      success: response?.success,
      message: response?.message,
      data: response?.data,
    });

    // 응답 데이터 검증
    if (!response) {
      console.error("[API_ERROR]", "Response is null or undefined");
      throw new Error("No data received from server");
    }

    // 백엔드 응답 구조: {success: true, message: "...", data: {...}}
    // success 필드 체크
    if (response.success === false) {
      throw new Error(response.message || "API request failed");
    }

    // data 필드 반환
    return response.data;
  },

  /**
   * 컨텐츠 수정
   */
  async update(
    id: number,
    data: UpdateChatContentDto
  ): Promise<ChatContentDto> {
    const apiUrl = `${PREFIX_PATH}/qna-content/${id}`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "PUT",
      id,
      data,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const response = await instance.put(apiUrl, data);

    console.log("[API_RESPONSE]", {
      url: apiUrl,
      dataStructure: response ? Object.keys(response) : "null/undefined",
      fullResponse: response,
      hasData: !!response,
      success: response?.success,
      message: response?.message,
      data: response?.data,
    });

    // 응답 데이터 검증
    if (!response) {
      console.error("[API_ERROR]", "Response is null or undefined");
      throw new Error("No data received from server");
    }

    // 백엔드 응답 구조: {success: true, message: "...", data: {...}}
    // success 필드 체크
    if (response.success === false) {
      throw new Error(response.message || "API request failed");
    }

    // data 필드 반환
    return response.data;
  },

  /**
   * 컨텐츠 삭제
   */
  async delete(id: number): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/qna-content/${id}`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "DELETE",
      id,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const response = await instance.delete(apiUrl);

    console.log("[API_RESPONSE]", {
      url: apiUrl,
      dataStructure: response ? Object.keys(response) : "null/undefined",
      fullResponse: response,
      hasData: !!response,
      success: response?.success,
      message: response?.message,
    });

    // 응답 데이터 검증
    if (!response) {
      console.error("[API_ERROR]", "Response is null or undefined");
      throw new Error("No data received from server");
    }

    // 백엔드 응답 구조: {success: true, message: "...", data: {...}}
    // success 필드 체크
    if (response.success === false) {
      throw new Error(response.message || "API request failed");
    }
  },

  /**
   * 컨텐츠 일괄 수정
   */
  async bulkUpdate(action: string, ids: number[]): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/qna-content/bulk-update`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "POST",
      action,
      ids,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const response = await instance.post(apiUrl, { action, ids });

    console.log("[API_RESPONSE]", {
      url: apiUrl,
      dataStructure: response ? Object.keys(response) : "null/undefined",
      fullResponse: response,
      hasData: !!response,
      success: response?.success,
      message: response?.message,
    });

    // 응답 데이터 검증
    if (!response) {
      console.error("[API_ERROR]", "Response is null or undefined");
      throw new Error("No data received from server");
    }

    // 백엔드 응답 구조: {success: true, message: "...", data: {...}}
    // success 필드 체크
    if (response.success === false) {
      throw new Error(response.message || "API request failed");
    }
  },

  /**
   * 카테고리별 컨텐츠 통계 조회
   */
  async getCategoryStats(): Promise<{ category: string; count: number }[]> {
    const apiUrl = `${PREFIX_PATH}/qna-content/categories`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "GET",
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    const response = await instance.get(apiUrl);

    console.log("[API_RESPONSE]", {
      url: apiUrl,
      dataStructure: response ? Object.keys(response) : "null/undefined",
      fullResponse: response,
      hasData: !!response,
      success: response?.success,
      message: response?.message,
      data: response?.data,
    });

    // 응답 데이터 검증
    if (!response) {
      console.error("[API_ERROR]", "Response is null or undefined");
      throw new Error("No data received from server");
    }

    // 백엔드 응답 구조: {success: true, message: "...", data: {...}}
    // success 필드 체크
    if (response.success === false) {
      throw new Error(response.message || "API request failed");
    }

    // data 필드 반환
    return response.data;
  },
};
